/*
 * SPDX-FileCopyrightText: 2022 CERN.
 * SPDX-FileCopyrightText: 2024 KTH Royal Institute of Technology.
 * SPDX-License-Identifier: MIT
 */

import { RequestActionContext } from "@js/invenio_requests/request/actions/context";
import PropTypes from "prop-types";
import React, { Component } from "react";
import { i18next } from "@translations/invenio_requests/i18next";
import Overridable from "react-overridable";
import { Modal } from "semantic-ui-react";
import Error from "../../components/Error";
import { CancelButton } from "../../components/Buttons";
import { RequestActionButton } from "./RequestActionButton";

export class RequestActionModal extends Component {
  constructor(props) {
    super(props);
    this.cancelBtnRef = React.createRef();
  }

  componentDidMount() {
    this.subscribeToContext();
  }

  subscribeToContext = () => {
    const { modalId } = this.props;
    const { modalOpen } = this.context;
    if (modalId in modalOpen) {
      modalOpen[modalId] = false;
    }
  };

  render() {
    const { action, handleActionClick, modalId, children, requestType } = this.props;
    const { modalOpen, loading, toggleModal, error, cleanError, className, size } =
      this.context;

    const currentModalOpen = modalOpen[modalId];

    const availableActions = {
      create: i18next.t("create"),
      submit: i18next.t("submit"),
      delete: i18next.t("delete"),
      accept: i18next.t("accept"),
      decline: i18next.t("decline"),
      cancel: i18next.t("cancel"),
      expire: i18next.t("expire"),
    };

    const actionLabel = availableActions[action] ? availableActions[action] : action;

    return (
      <Overridable id="InvenioRequests.RequestActionModal.layout" {...this.props}>
        {/* currentModalOpen prevents mounting multiple instances */}
        {currentModalOpen && (
          <Modal aria-label={action} role="dialog" id={modalId} open={currentModalOpen}>
            <Modal.Header as="h2" className="capitalize-first-char">
              <Overridable id={`RequestActionModal.title.${action}`}>
                <span>{i18next.t("{{action}} request", { action: actionLabel })}</span>
              </Overridable>
            </Modal.Header>
            <Modal.Content>
              <Modal.Description>
                {error && <Error error={error.message} />}
                {children}
              </Modal.Description>
            </Modal.Content>
            <Modal.Actions>
              <CancelButton
                ref={this.cancelBtnRef}
                onClick={() => {
                  cleanError();
                  toggleModal(modalId, false);
                }}
                loading={loading}
                disabled={loading}
                floated="left"
                size="medium"
              />
              <RequestActionButton
                action={action}
                handleActionClick={handleActionClick}
                loading={loading}
                className={className}
                size={size}
                requestType={requestType}
              />
            </Modal.Actions>
          </Modal>
        )}
      </Overridable>
    );
  }
}

RequestActionModal.contextType = RequestActionContext;

RequestActionModal.propTypes = {
  action: PropTypes.string.isRequired,
  handleActionClick: PropTypes.func.isRequired,
  modalId: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  requestType: PropTypes.string.isRequired,
  loading: PropTypes.bool,
  modalOpen: PropTypes.bool,
};

RequestActionModal.defaultProps = {
  loading: false,
  modalOpen: false,
};

export default Overridable.component(
  "InvenioRequests.RequestActionModal",
  RequestActionModal
);
